import { useReveal } from "../hooks/useReveal";
import projects from "../data/projects";

const Projects = () => {
  const featured = projects[0];
  const rest = projects.slice(1);
  const [ref, visible] = useReveal(0.08);

  return (
    <section ref={ref} className="w-full px-2 md:px-8">
      <div className="max-w-[1500px] mx-auto py-20 px-4 md:px-12">
        {/* Section header */}
        <div className={`flex flex-col gap-4 mb-12 ${visible ? "reveal-up" : "reveal-hidden"}`}>
          <p className="text-2xl font-black uppercase tracking-tight">
            Flagship Systems
            <span className="text-orange-600">.</span>
          </p>
          <div className={`w-16 h-1 bg-black ${visible ? "line-grow delay-200" : "reveal-hidden"}`} />
          <p className="text-sm max-w-[560px]">
            Real products I've designed, built, and shipped end-to-end. Each one
            represents full technical ownership — from architecture to deployment.
          </p>
        </div>

        {/* Featured project — big card */}
        <a
          href={`/projects/${featured.slug}`}
          className={`group block border-3 border-black bg-orange-600 text-white shadow-[8px_8px_0px_0px_#000] hover:shadow-[8px_8px_0px_0px_#78350f] transition-all duration-300 mb-10 ${visible ? "reveal-scale delay-300" : "reveal-hidden"}`}
        >
          <div className="flex flex-col lg:flex-row">
            {/* Left — big title area */}
            <div className="lg:w-[50%] p-8 md:p-10 flex flex-col justify-between gap-6 border-b-3 lg:border-b-0 lg:border-r-3 border-black">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-orange-200 block mb-3">
                  Featured System
                </span>
                <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tighter leading-[0.9] group-hover:text-black transition-colors">
                  {featured.name}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {featured.stack.map((tech) => (
                  <span
                    key={tech}
                    className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border border-white"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — description + meta */}
            <div className="lg:w-[50%] p-8 md:p-10 flex flex-col justify-between gap-6 bg-white text-black">
              <div className="flex flex-col gap-4">
                <p className="font-bold leading-relaxed">
                  {featured.tagline}
                </p>
                <p className="text-sm leading-relaxed">
                  {featured.description}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-600 block mb-1">
                    Role
                  </span>
                  <p className="text-xs font-bold">{featured.role}</p>
                </div>
                <span className="text-3xl font-black text-gray-300 group-hover:text-orange-600 group-hover:translate-x-1 transition-all duration-200">
                  →
                </span>
              </div>
            </div>
          </div>
        </a>

        {/* Other projects — side by side, alternating accent */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {rest.map((project, i) => (
            <a
              key={project.slug}
              href={`/projects/${project.slug}`}
              className={`group block border-3 border-black shadow-[5px_5px_0px_0px_#000] hover:shadow-[5px_5px_0px_0px_#ea580c] hover:border-orange-600 transition-all duration-300 ${
                i === 0 ? "md:mt-12" : ""
              } ${visible ? `reveal-up delay-${(i + 5) * 100}` : "reveal-hidden"}`}
            >
              {/* Header */}
              <div className={`p-5 border-b-3 border-black ${
                i % 2 === 0 ? "bg-gray-100" : "bg-amber-50"
              }`}>
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400 block">
                  System {String(i + 2).padStart(2, "0")}
                </span>
                <h3 className="text-xl font-black uppercase tracking-tight group-hover:text-orange-600 transition-colors">
                  {project.name}
                </h3>
              </div>

              {/* Body */}
              <div className="p-5 bg-white flex flex-col gap-4">
                <p className="text-sm font-bold">{project.tagline}</p>
                <p className="text-sm leading-relaxed">{project.description}</p>

                <div className="flex flex-wrap gap-2 mt-auto">
                  {project.stack.map((tech) => (
                    <span
                      key={tech}
                      className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border border-black"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400">
                    {project.role}
                  </p>
                  {project.link && (
                    <span className="text-xs font-bold border-b border-orange-600 text-orange-600">
                      {project.link.replace(/^https?:\/\//, "")}
                    </span>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
